import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import LogoutConfirmation from "../../components/Modals/LogoutConfirmation";
import "./SuperSidebar.css";

const SuperSidebar = ({ onSidebarToggle }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  /* ---------- helpers ---------- */
  const isActive = (path) => location.pathname.startsWith(path);
  const toggle = () => setIsOpen((prev) => !prev);
  const close = () => setIsOpen(false);

  useEffect(() => {
    if (onSidebarToggle) {
      onSidebarToggle(isOpen);
    }
  }, [isOpen, onSidebarToggle]);

  const handleLogoutClick = () => setShowLogoutModal(true);

  const handleLogoutConfirm = async () => {
    setLogoutLoading(true);
    try {
      await logout();
      navigate("/login");
      close();
    } catch (err) {
      console.error("Logout failed", err);
    } finally {
      setLogoutLoading(false);
      setShowLogoutModal(false);
    }
  };

  const handleLogoutCancel = () => setShowLogoutModal(false);

  const sidebarCls = `superadmin-sidebar ${isOpen ? "open" : ""}`;

  return (
    <>
      {/* ─── Hamburger button (visible only when closed) ─── */}
      {!isOpen && (
        <button
          className="super-hamburger-menu"
          onClick={toggle}
          aria-label="Open sidebar"
        >
          <span className="super-hamburger-line" />
          <span className="super-hamburger-line" />
          <span className="super-hamburger-line" />
        </button>
      )}

      {/* ─── Dark overlay (mobile only) ─── */}
      {isOpen && window.innerWidth <= 768 && (
        <div className="super-sidebar-overlay" onClick={close} />
      )}

      {/* ─── Sidebar ─── */}
      <aside className={sidebarCls}>

        {/* Header with Logo */}
        <div className="super-sidebar-header">
          <div className="super-logo-container">
            <img
              src="/logo.png"
              alt="Company Logo"
              className="super-sidebar-logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/default-logo.png";
              }}
            />
            <button
              className="super-sidebar-close-btn"
              onClick={close}
              aria-label="Close sidebar"
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          <ul className="nav-links-container">

            {/* ── Overview ── */}
            <li className={isActive("/super-admin/dashboard") ? "active" : ""}>
              <NavLink
                to="/super-admin/dashboard"
                className="nav-link"
                onClick={window.innerWidth <= 768 ? close : undefined}
              >
                <i className="bi bi-speedometer2" />
                <span>Overview</span>
              </NavLink>
            </li>

            {/* ── Organizations ── */}
            <li className={isActive("/super-admin/masters/companies") ? "active" : ""}>
              <NavLink
                to="/super-admin/masters/companies"
                className="nav-link"
                onClick={window.innerWidth <= 768 ? close : undefined}
              >
                <i className="bi bi-building" />
                <span>Organizations</span>
              </NavLink>
            </li>


            {/* ── Permissions ── */}
            <li className={isActive("/super-admin/access-requests") ? "active" : ""}>
              <NavLink
                to="/super-admin/access-requests"
                className="nav-link"
                onClick={window.innerWidth <= 768 ? close : undefined}
              >
                <i className="bi bi-shield-lock" />
                <span>Permissions</span>
              </NavLink>
            </li>

            {/* ── Users ── */}
            <li className={isActive("/super-admin/users") ? "active" : ""}>
              <NavLink
                to="/super-admin/users"
                className="nav-link"
                onClick={window.innerWidth <= 768 ? close : undefined}
              >
                <i className="bi bi-person-badge" />
                <span>Users</span>
              </NavLink>
            </li>

            {/* ── Reports & Analytics ── */}
            <li className={isActive("/super-admin/reports") ? "active" : ""}>
              <NavLink
                to="/super-admin/reports"
                className="nav-link"
                onClick={window.innerWidth <= 768 ? close : undefined}
              >
                <i className="bi bi-file-earmark-text" />
                <span>Reports &amp; Analytics</span>
              </NavLink>
            </li>

 
           
            {/* ── Activity Log ── */}
            <li className={isActive("/super-admin/audit-logs") ? "active" : ""}>
              <NavLink
                to="/super-admin/audit-logs"
                className="nav-link"
                onClick={window.innerWidth <= 768 ? close : undefined}
              >
                <i className="bi bi-clipboard-data" />
                <span>Activity Log</span>
              </NavLink>
            </li>

            {/* ── Feedback ── */}
            <li className={isActive("/super-admin/feedback") ? "active" : ""}>
              <NavLink
                to="/super-admin/feedback"
                className="nav-link"
                onClick={window.innerWidth <= 768 ? close : undefined}
              >
                <i className="bi bi-chat-dots" />
                <span>Feedback</span>
              </NavLink>
            </li>

            {/* ── Settings ── */}
            <li className={isActive("/super-admin/settings") ? "active" : ""}>
              <NavLink
                to="/super-admin/settings"
                className="nav-link"
                onClick={window.innerWidth <= 768 ? close : undefined}
              >
                <i className="bi bi-sliders" />
                <span>Settings</span>
              </NavLink>
            </li>

          </ul>
        </nav>

        <div className="logout-container">
          <button className="logout-btn" onClick={handleLogoutClick}>
            <i className="bi bi-box-arrow-right" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ─── Logout Confirmation Modal ─── */}
      {showLogoutModal && (
        <LogoutConfirmation
          onClose={handleLogoutCancel}
          onConfirm={handleLogoutConfirm}
          loading={logoutLoading}
        />
      )}
    </>
  );
};

export default SuperSidebar;